export type DrivingForce = "fossil_fuel" | "energy" | string;
export type Range = number;
export type Capacity = number;
export type EnergyPrice = number;
export type Weight = number;

export class Vehicle {
    static divideEnergyBy = 1000;
    energyPrice: EnergyPrice;

    id: number;
    brand: string;
    model: string;
    drivingForce: DrivingForce;
    fuelType: string;
    range: Range;
    capacity: Capacity;
    weight: Weight;

    constructor(properties: {
        id: number;
        brand: string;
        model: string;
        drivingForce: DrivingForce;
        range: Range;
        capacity: Capacity;
        weight: Weight;
        energyPrice: EnergyPrice;
    }) {
        this.id = properties.id;
        this.brand = properties.brand;
        this.model = properties.model;
        this.drivingForce = properties.drivingForce;
        this.range = properties.range;
        if (properties.drivingForce === "energy") {
            this.capacity = properties.capacity * Vehicle.divideEnergyBy;
        } else {
            this.capacity = properties.capacity;
        }
        this.weight = properties.weight;

        this.energyPrice = properties.energyPrice;
    }

    public sanitizeEnergyNumber(number: number, timesOperator = false) {
        if (this.drivingForce === "energy") {
            if (timesOperator) {
                return number * Vehicle.divideEnergyBy;
            }
            return number / Vehicle.divideEnergyBy;
        }

        return number;
    }

    public kmUsageRaw() {
        return this.range / this.capacity;
    }

    public kmUsage() {
        return this.sanitizeEnergyNumber(this.kmUsageRaw(), true);
    }

    public kmPriceRaw() {
        return this.energyPrice / this.kmUsageRaw();
    }

    public kmPrice() {
        return this.sanitizeEnergyNumber(this.kmPriceRaw());
    }

    public fullRefillPriceRaw() {
        return this.capacity * this.energyPrice;
    }

    public fullRefillPrice() {
        return this.sanitizeEnergyNumber(this.fullRefillPriceRaw());
    }

    public rangeByAmount(price: number = 100) {
        return price / this.kmPrice();
    }

    public costForRange(range: Range) {
        return this.kmPrice() * range;
    }

    public percentageUsageVehicle(cargoWeight: number = 100) {
        return this.weight / ((this.weight + cargoWeight) / 100);
    }

    public percentageUsageCargo(cargoWeight: number = 100) {
        return cargoWeight / ((this.weight + cargoWeight) / 100);
    }

    public name() {
        return this.brand + " " + this.model;
    }
}

export const cars = [
    {
        id: 1,
        brand: "VW",
        model: "ID5",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 82,
        range: 620,
        weight: 2117,
    },
    {
        id: 2,
        brand: "Kia",
        model: "EV6",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 58,
        range: 528,
        weight: 1875,
    },
    {
        id: 3,
        brand: "Tesla",
        model: "Model S",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 75,
        range: 480,
        weight: 2062,
    },
    {
        id: 4,
        brand: "Tesla",
        model: "Model 3",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 50,
        range: 547,
        weight: 1819,
    },
    {
        id: 5,
        brand: "Tesla",
        model: "Model X",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 75,
        range: 375,
        weight: 2301,
    },
    {
        id: 6,
        brand: "Tesla",
        model: "Model Y",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 75,
        range: 393,
        weight: 1956,
    },
    {
        id: 7,
        brand: "Polestar",
        model: "2",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 69,
        range: 480,
        weight: 1938,
    },
    {
        id: 8,
        brand: "Porsche",
        model: "Taycan",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 79.2,
        range: 400,
        weight: 2080,
    },
    {
        id: 9,
        brand: "Audi",
        model: "E-tron GT",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 93.4,
        range: 434,
        weight: 2254,
    },
    {
        id: 10,
        brand: "Citroen",
        model: "C3 1.2 Benzin",
        drivingForce: "fossil_fuel",
        fuelType: "Benzin",
        capacity: 45,
        range: 749.97,
        weight: 1280,
    },
    {
        id: 11,
        brand: "Peugeot",
        model: "208 1.2 Benzin",
        drivingForce: "fossil_fuel",
        fuelType: "Benzin",
        capacity: 44,
        range: 800.8,
        weight: 1215,
    },
];

export const vehicles = [
    {
        id: 100,
        brand: "NIU",
        model: "MQi GT EVO - 100 km/t.",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 3.74,
        range: 75,
        weight: 128,
    },
    {
        id: 101,
        brand: "NIU",
        model: "NQi GTS 60V35Ah - 70 km/t.",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 4.2,
        range: 120,
        weight: 113,
    },
    {
        id: 102,
        brand: "NIU",
        model: "MQi+ Sport 48V42Ah - 30 km/t.",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 2.01,
        range: 70,
        weight: 72,
    },
    {
        id: 103,
        brand: "NIU",
        model: "NQi Sport 60V35Ah - 45 km/t.",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 2.1,
        range: 70,
        weight: 99,
    },
    {
        id: 104,
        brand: "NIU",
        model: "NQi GTS Cargo 2x60V35Ah - 45 km/t.",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 4.2,
        range: 120,
        weight: 110,
    },
    {
        id: 105,
        brand: "NIU",
        model: "NQi Cargo 2x60V35Ah - 30 km/t.",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 4.2,
        range: 135,
        weight: 99,
    },
    {
        id: 106,
        brand: "NIU",
        model: "UQi Sport 48V21Ah - 30 km/t.",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 1.0,
        range: 37,
        weight: 57,
    },
    {
        id: 107,
        brand: "NIU",
        model: "KQi3 Pro - El-løbehjul",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 0.486,
        range: 50,
        weight: 20,
    },
    //Check form here
    {
        id: 108,
        brand: "Tromox",
        model: "Ukko S - 93 km/t.",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 3.96,
        range: 160,
        weight: 110,
    },
    {
        id: 109,
        brand: "Tromox",
        model: "Mino - 30 km/t.",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 1.86,
        range: 90,
        weight: 68,
    },
    {
        id: 110,
        brand: "Talaria",
        model: "Sting - 45 km/t.",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 2.28,
        range: 70,
        weight: 68,
    },
    {
        id: 111,
        brand: "Ive-car",
        model: "Kabinescooter - stort batteri",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 5.04,
        range: 109,
        weight: 250,
    },
    {
        id: 112,
        brand: "Ape 50",
        model: "Van - 30 km/t.",
        drivingForce: "fossil_fuel",
        fuelType: "Benzin",
        capacity: 10,
        range: 220,
        weight: 250,
    },
    {
        id: 113,
        brand: "MBM",
        model: "El-cykel",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 0.498,
        range: 120,
        weight: 25,
    },
];

const tooManyVehicles = [
    {
        id: 100,
        brand: "NIU",
        model: "MQi GT EVO",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 3.74,
        range: 75,
        weight: 128,
    },
    {
        id: 101,
        brand: "NIU",
        model: "MQi GT 2x48V31Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 2.97,
        range: 80,
        weight: 115,
    },
    {
        id: 102,
        brand: "NIU",
        model: "MQi GT 2x48V42Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 4.03,
        range: 110,
        weight: 115,
    },
    {
        id: 103,
        brand: "NIU",
        model: "MQi+ Sport 48V31Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 1.48,
        range: 55,
        weight: 72,
    },
    {
        id: 104,
        brand: "NIU",
        model: "MQi+ Sport 48V42Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 2.01,
        range: 70,
        weight: 72,
    },
    {
        id: 105,
        brand: "NIU",
        model: "NQi GTS 60V26Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 3.12,
        range: 90,
        weight: 113,
    },
    {
        id: 106,
        brand: "NIU",
        model: "NQi GTS 60V35Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 4.2,
        range: 120,
        weight: 113,
    },
    {
        id: 107,
        brand: "NIU",
        model: "NQi Sport 60V26Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 1.5,
        range: 50,
        weight: 99,
    },
    {
        id: 108,
        brand: "NIU",
        model: "NQi Sport 60V35Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 2.1,
        range: 70,
        weight: 99,
    },
    {
        id: 109,
        brand: "NIU",
        model: "NQi GTS Cargo 2x60V26Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 3.12,
        range: 90,
        weight: 110,
    },
    {
        id: 110,
        brand: "NIU",
        model: "NQi GTS Cargo 2x60V35Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 4.2,
        range: 120,
        weight: 110,
    },
    {
        id: 111,
        brand: "NIU",
        model: "NQi Cargo 2x60V26Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 3.1,
        range: 100,
        weight: 99,
    },
    {
        id: 112,
        brand: "NIU",
        model: "NQi Cargo 2x60V35Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 4.2,
        range: 135,
        weight: 99,
    },
    {
        id: 113,
        brand: "NIU",
        model: "UQi Sport 48V21Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 1.0,
        range: 37,
        weight: 57,
    },
    {
        id: 114,
        brand: "NIU",
        model: "UQi GT 48V31Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 1.48,
        range: 50,
        weight: 75,
    },
    {
        id: 115,
        brand: "NIU",
        model: "UQi GT 48V42Ah",
        drivingForce: "energy",
        fuelType: "El",
        capacity: 2.01,
        range: 60,
        weight: 75,
    },
];

export const allVehicles = [...cars, ...vehicles];
